import { useQuery } from '@tanstack/vue-query'
import { computed, type ComputedRef } from 'vue'
import { axios } from '@/lib'
import { buildQueryParams, formatSearchTerm } from '@/lib/api'
import type { Notification, NotificationChannel, ListWrap } from './types'
import type { Pagination } from '@/types/shared.types'

export interface GetNotificationsOptions extends Partial<Pagination> {
  limit?: string
  skip?: string
  term?: string
}

export async function getNotifications(options: GetNotificationsOptions = {}) {
  const { limit, skip, term } = options
  const endpoint = '/api/notifications'

  const params = buildQueryParams({
    limit,
    skip,
    term: formatSearchTerm(term),
  })

  const response = await axios.get<ListWrap<Notification>>(endpoint, { params })
  return response.data
}

export async function getNotificationChannels(userId?: string) {
  const endpoint =
    userId && userId !== 'self'
      ? `/api/notifications/channels/${userId}`
      : '/api/notifications/channels/self'
  const response = await axios.get<ListWrap<NotificationChannel>>(endpoint)
  return response.data
}

export function useNotificationsQuery(
  options: ComputedRef<GetNotificationsOptions> | GetNotificationsOptions = {},
) {
  return useQuery({
    queryKey: computed(() => {
      const opts = 'value' in options ? options.value : options
      const { limit, skip, sort, term } = opts
      return ['notifications', { limit, skip, sort, term }]
    }),
    queryFn: () => {
      const opts = 'value' in options ? options.value : options
      return getNotifications(opts)
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 60 * 1000, // Poll every 60 seconds for new notifications
  })
}

export function useNotificationChannelsQuery(userId?: string) {
  return useQuery({
    queryKey: ['notification-channels', userId || 'self'],
    queryFn: () => getNotificationChannels(userId),
    staleTime: 10 * 60 * 1000, // 10 minutes
  })
}
